import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import { db } from "../firebaseConfig";
import "../css/chapterDashboard.css";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Helmet } from "@dr.pogodin/react-helmet";

// Reusable panel version of the talks dashboard.
// `embedded=true` trims the top "utility" buttons so it can fit inside another dashboard.
export function TalksDashboardPanel({ embedded = false, controls = "full" }) {
    const [talks, setTalks] = useState([]);
    const [loading, setLoading] = useState(true);

    const [role, setRole] = useState(null);
    const normalizedRole = role?.toLowerCase();

    const [users, setUsers] = useState([]);
    const [usersById, setUsersById] = useState({});

    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesError, setMessagesError] = useState("");
    const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);
    const [activeMessage, setActiveMessage] = useState(null);

    const [activeTab, setActiveTab] = useState("unpublished"); // 'unpublished' | 'published' | 'mine'
    const [uid, setUid] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    // Permission helpers
    const roleRank = { publisher: 3, deacon: 2, writer: 1 };
    const getRank = (r) => roleRank[r] || 0;
    const canEditByStatusAndRole = (status, roleName) => {
        const s = (status || "").toLowerCase();
        const rank = getRank(roleName);
        // Draft and Ready for Deacons: anyone (writer, deacon, publisher)
        if (s === "draft" || s === "ready for deacons") return rank >= 1;
        // Ready to Publish: only deacon and publisher
        if (s === "ready to publish" || s === "ready for publish" || s === "ready for publisher") return rank >= 2;
        // Published: only publisher
        if (s === "published") return rank >= 3;
        return false;
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    // load role from Firestore users collection
                    const userRef = doc(db, "users", user.uid);
                    const userSnap = await getDoc(userRef);
                    setRole(userSnap.exists() ? userSnap.data().role : null);
                    setUid(user.uid);
                } catch (e) {
                    console.error("Error loading user role:", e);
                    setRole(null);
                    setUid(null);
                }
            } else {
                setRole(null);
                setUid(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        async function fetchUsers() {
            try {
                const snap = await getDocs(collection(db, "users"));
                const list = snap.docs.map((d) => {
                    const data = d.data() || {};
                    const displayName = data.displayName || data.name || "";
                    return {
                        id: d.id,
                        email: data.email || "",
                        role: data.role || "viewer",
                        displayName,
                    };
                });
                const map = list.reduce((acc, u) => {
                    acc[u.id] = u.displayName;
                    return acc;
                }, {});
                setUsers(list);
                setUsersById(map);
            } catch (e) {
                console.error("Error loading users:", e);
                setUsers([]);
                setUsersById({});
            }
        }
        fetchUsers();
    }, []);

    useEffect(() => {
        async function fetchTalks() {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "talks"));
                const data = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                }));
                // sort by updatedAt descending if available
                data.sort((a, b) => {
                    const ta = a.updatedAt?.toDate?.() || new Date(0);
                    const tb = b.updatedAt?.toDate?.() || new Date(0);
                    return tb - ta;
                });
                setTalks(data);
            } catch (err) {
                console.error("Error loading talks:", err);
            } finally {
                setLoading(false);
            }
        }
        fetchTalks();
    }, []);

    const fetchMessages = async () => {
        setMessagesLoading(true);
        setMessagesError("");
        try {
            const snap = await getDocs(collection(db, "contacts"));
            const list = snap.docs.map((d) => {
                const data = d.data() || {};
                return {
                    id: d.id,
                    email: data.email || "",
                    firstName: data.firstName || "",
                    lastName: data.lastName || "",
                    message: data.message || "",
                    createdAt: data.createdAt,
                    read: data.read || false,
                };
            });
            // Sort newest first by createdAt if present
            list.sort((a, b) => {
                const ta = a.createdAt?.toDate?.() || new Date(0);
                const tb = b.createdAt?.toDate?.() || new Date(0);
                return tb - ta;
            });
            setMessages(list);
        } catch (e) {
            console.error("Error loading messages:", e);
            setMessagesError("Failed to load messages.");
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    };

    const openMessagesModal = async () => {
        setIsMessagesModalOpen(true);
        await fetchMessages();
    };

    const closeMessagesModal = () => {
        setIsMessagesModalOpen(false);
        setActiveMessage(null);
    };

    const handleOpenMessage = (msg) => {
        setActiveMessage(msg);
    };

    const handleCloseMessageModal = () => {
        setActiveMessage(null);
    };

    const handleMarkMessageRead = async (id) => {
        try {
            const msgRef = doc(db, "contacts", id);
            await updateDoc(msgRef, { read: true });
            setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
        } catch (e) {
            console.error("Error marking message as read:", e);
        }
    };

    const unreadCount = messages.filter((m) => !m.read).length;

    const filteredTalks = talks.filter((talk) => talk.title?.toLowerCase().includes(searchTerm.toLowerCase()));

    const displayedTalks = [...filteredTalks].sort((a, b) => {
        if (!sortColumn) return 0;
        let aVal;
        let bVal;
        if (sortColumn === "updatedAt") {
            aVal = a.updatedAt?.toDate?.().getTime() || 0;
            bVal = b.updatedAt?.toDate?.().getTime() || 0;
        } else if (sortColumn === "author") {
            const aLabel = usersById[a.authorId] || a.authorId || "";
            const bLabel = usersById[b.authorId] || b.authorId || "";
            aVal = aLabel.toString().toLowerCase();
            bVal = bLabel.toString().toLowerCase();
        } else {
            aVal = (a[sortColumn] || "").toString().toLowerCase();
            bVal = (b[sortColumn] || "").toString().toLowerCase();
        }
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
    });

    const tabFilteredTalks = displayedTalks.filter((talk) => {
        const status = (talk.status || "").toLowerCase();
        const isPublished = status === "published";
        if (activeTab === "published") return isPublished;
        if (activeTab === "mine") return uid ? talk.authorId === uid : false;
        // default: unpublished (anything not published)
        return !isPublished;
    });

    return (
        <div className={embedded ? "nd-talks-embed" : "dashboard-talks-standalone"}>

            <div style={{ marginBottom: "1rem", display: "flex" }}>
                <input
                    type="text"
                    placeholder="Search by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="dashboard-search"
                    style={
                        embedded
                            ? {
                                flex: 1,
                                padding: "0.55rem 0.9rem",
                                borderRadius: "10px",
                                border: "1px solid rgba(15, 23, 42, 0.14)",
                                fontSize: "0.95rem",
                                boxSizing: "border-box",
                            }
                            : {
                                flex: 1,
                                padding: "0.5rem 1rem",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                                fontSize: "1rem",
                            }
                    }
                />
            </div>

            {loading ? (
                <p>Loading talks…</p>
            ) : (
                <>
                    <div
                        role="tablist"
                        aria-label="Talk status tabs"
                        style={
                            embedded
                                ? { display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }
                                : { display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }
                        }
                    >
                        <button
                            role="tab"
                            aria-selected={activeTab === "unpublished"}
                            onClick={() => setActiveTab("unpublished")}
                            className={embedded ? "nd-btn" : "subscribe-button"}
                            style={
                                embedded
                                    ? { opacity: activeTab === "unpublished" ? 1 : 0.7 }
                                    : { padding: "0.35rem 0.75rem", opacity: activeTab === "unpublished" ? 1 : 0.7 }
                            }
                            type="button"
                        >
                            Unpublished
                        </button>
                        <button
                            role="tab"
                            aria-selected={activeTab === "published"}
                            onClick={() => setActiveTab("published")}
                            className={embedded ? "nd-btn" : "subscribe-button"}
                            style={
                                embedded
                                    ? { opacity: activeTab === "published" ? 1 : 0.7 }
                                    : { padding: "0.35rem 0.75rem", opacity: activeTab === "published" ? 1 : 0.7 }
                            }
                            type="button"
                        >
                            Published
                        </button>
                        <button
                            role="tab"
                            aria-selected={activeTab === "mine"}
                            onClick={() => setActiveTab("mine")}
                            className={embedded ? "nd-btn" : "subscribe-button"}
                            style={
                                embedded
                                    ? { opacity: activeTab === "mine" ? 1 : 0.7 }
                                    : { padding: "0.35rem 0.75rem", opacity: activeTab === "mine" ? 1 : 0.7 }
                            }
                            type="button"
                        >
                            My Talks
                        </button>
                    </div>

                    <div style={embedded ? { overflowX: "auto" } : undefined}>
                        <table className="dashboard-table" style={embedded ? { width: "100%" } : undefined}>
                            <thead>
                                <tr>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("title")}>
                                        Title {sortColumn === "title" ? (sortDirection === "asc" ? "↑" : "↓") : ""}
                                    </th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("author")}>
                                        Author {sortColumn === "author" ? (sortDirection === "asc" ? "↑" : "↓") : ""}
                                    </th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>
                                        Status {sortColumn === "status" ? (sortDirection === "asc" ? "↑" : "↓") : ""}
                                    </th>
                                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("updatedAt")}>
                                        Last Updated {sortColumn === "updatedAt" ? (sortDirection === "asc" ? "↑" : "↓") : ""}
                                    </th>
                                    <th>View</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tabFilteredTalks.map((talk) => {
                                    const canEdit = canEditByStatusAndRole(talk.status, normalizedRole);
                                    return (
                                        <tr key={talk.id}>
                                            <td>{talk.title || "Untitled"}</td>
                                            <td>{usersById[talk.authorId] || talk.authorId || "-"}</td>
                                            <td>{talk.status}</td>
                                            <td>{talk.updatedAt ? talk.updatedAt.toDate().toLocaleString() : "-"}</td>
                                            <td>
                                                <Link to={`/talk/${talk.id}`} className={embedded ? "nd-btn" : "subscribe-button"}>
                                                    View
                                                </Link>
                                            </td>
                                            <td>
                                                {canEdit ? (
                                                    <Link to={`/editor/${talk.id}`} className={embedded ? "nd-btn" : "subscribe-button"}>
                                                        Edit
                                                    </Link>
                                                ) : (
                                                    <span style={{ color: "#999" }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Messages modals */}
            {isMessagesModalOpen && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0,0,0,0.4)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        className="subscribe-hero message-modal-card"
                        style={{
                            maxWidth: "800px",
                            width: "90%",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            padding: "1.5rem 1.75rem",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                            <h2 style={{ margin: 0 }}>Contact Messages</h2>
                            <button
                                type="button"
                                onClick={closeMessagesModal}
                                className={embedded ? "nd-btn" : "subscribe-button"}
                                style={embedded ? undefined : { padding: "0.25rem 0.6rem", fontSize: "0.9rem" }}
                            >
                                Close
                            </button>
                        </div>

                        {messagesLoading && <p>Loading messages…</p>}
                        {messagesError && <p style={{ color: "red" }}>{messagesError}</p>}

                        {!messagesLoading && !messagesError && messages.length === 0 && <p>No messages have been received yet.</p>}

                        {!messagesLoading && messages.length > 0 && (
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                {messages.map((msg) => {
                                    const created = msg.createdAt?.toDate?.().toLocaleString() || "Unknown date";
                                    const isRead = !!msg.read;
                                    const preview = msg.message.length > 140 ? msg.message.slice(0, 140) + "…" : msg.message;
                                    return (
                                        <li
                                            key={msg.id}
                                            style={{
                                                border: "1px solid #e0e0e0",
                                                borderRadius: "6px",
                                                padding: "0.75rem 0.9rem",
                                                marginBottom: "0.6rem",
                                                backgroundColor: isRead ? "#f9fafb" : "#ffffff",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "0.4rem",
                                                }}
                                            >
                                                <div>
                                                    <strong>
                                                        {msg.firstName} {msg.lastName}
                                                    </strong>{" "}
                                                    <span style={{ color: "#555" }}>({msg.email})</span>
                                                    <div style={{ fontSize: "0.85rem", color: "#777" }}>{created}</div>
                                                </div>
                                                <div style={{ display: "flex", gap: "0.4rem" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenMessage(msg)}
                                                        className={embedded ? "nd-btn" : "subscribe-button"}
                                                        style={embedded ? undefined : { padding: "0.2rem 0.5rem", fontSize: "0.85rem" }}
                                                    >
                                                        View
                                                    </button>
                                                    {!isRead && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMarkMessageRead(msg.id)}
                                                            className={embedded ? "nd-btn" : "subscribe-button"}
                                                            style={embedded ? undefined : { padding: "0.2rem 0.5rem", fontSize: "0.85rem" }}
                                                        >
                                                            Mark Read
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="message-modal-body">{preview}</div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {activeMessage && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0,0,0,0.55)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1100,
                    }}
                >
                    <div
                        className="subscribe-hero message-modal-card"
                        style={{
                            maxWidth: "700px",
                            width: "90%",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            padding: "1.5rem 1.75rem",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "1rem",
                            }}
                        >
                            <div>
                                <h2 style={{ margin: 0 }}>
                                    Message from {activeMessage.firstName} {activeMessage.lastName}
                                </h2>
                                <div style={{ fontSize: "0.9rem", color: "#555" }}>{activeMessage.email}</div>
                                <div style={{ fontSize: "0.85rem", color: "#777" }}>
                                    {activeMessage.createdAt?.toDate?.().toLocaleString() || "Unknown date"}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                                {!activeMessage.read && (
                                    <button
                                        type="button"
                                        onClick={() => handleMarkMessageRead(activeMessage.id)}
                                        className={embedded ? "nd-btn" : "subscribe-button"}
                                        style={embedded ? undefined : { padding: "0.25rem 0.6rem", fontSize: "0.85rem" }}
                                    >
                                        Mark Read
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleCloseMessageModal}
                                    className={embedded ? "nd-btn" : "subscribe-button"}
                                    style={embedded ? undefined : { padding: "0.25rem 0.6rem", fontSize: "0.85rem" }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                        <div className="message-modal-body" style={{ whiteSpace: "pre-wrap" }}>
                            {activeMessage.message}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Dashboard() {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>
            <Helmet>
                <title>Dashboard</title>
            </Helmet>
            {isMobile ? (
                <>
                    <Navbar />
                    <main className="subscribe-page dashboard-page" style={{ paddingTop: "1rem", textAlign: "center" }}>
                        <div className="subscribe-hero" style={{ padding: "2rem" }}>
                            <h2>This dashboard is best viewed on a desktop or larger screen.</h2>
                            <p>Please switch to a desktop to manage talks.</p>
                        </div>
                    </main>
                    <Footer />
                </>
            ) : (
                <>
                    <Navbar />
                    <main className="subscribe-page dashboard-page" style={{ paddingTop: "1rem" }}>
                        <div className="subscribe-hero" style={{ padding: "1.5rem 2rem" }}>
                            <TalksDashboardPanel embedded={false} />
                        </div>
                    </main>
                    <Footer />
                </>
            )}
        </>
    );
}